var ProductsCarousel = class extends HTMLElement {
  connectedCallback() {
    if (this._$track) {
      return; // already initialised (script copies re-invoke this)
    }
    this._track = this.querySelector(".products-carousel__track");
    if (!this._track || !window.jQuery || !window.jQuery.fn.slick) {
      return;
    }
    this._$track = window.jQuery(this._track);
    this._slideCount = this.querySelectorAll(".products-carousel__slide").length;
    this._prevRevealed = false;
    this._$track.on("init afterChange", () => this._updateAriaHiddenFocusable());
    this._$track.on("beforeChange", () => this._revealPrev());
    this._bindNavigation();
    this._bindResize();
    this._update(false);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this._onResize);
    if (this._$track && this._$track.hasClass("slick-initialized")) {
      this._$track.slick("unslick");
    }
  }

  /** How many slides fit on screen at the current width (mirrors the CSS tiers). */
  _slidesVisible() {
    const visible = parseInt(this.dataset.productsVisible, 10) || 4;
    const width = window.innerWidth;
    if (width <= 768) {
      return 2; // 1.5 slides shown; carousel/buttons from 3+ products
    }
    if (width <= 1024) {
      return Math.max(2, visible - 1);
    }
    return visible;
  }

  _needsCarousel() {
    return this._slideCount > this._slidesVisible();
  }

  /** The nav buttons centre on the product image, not the full card. The
      CSS fallback derives the image height from the slide-width formula;
      measuring the rendered figure here keeps the buttons exact regardless
      of scrollbars, zoom or card-internal spacing. */
  _positionNav() {
    const viewport = this.querySelector(".products-carousel__viewport");
    const figure = viewport && viewport.querySelector(".product-card__figure");
    if (!figure) {
      return;
    }
    const figureRect = figure.getBoundingClientRect();
    const offset = figureRect.top - viewport.getBoundingClientRect().top + figureRect.height / 2;
    this.style.setProperty("--pc-nav-center", `${offset}px`);
    this.classList.add("products-carousel--nav-ready");
  }

  /** Init/destroy slick and toggle the nav buttons to match whether the
      products actually overflow at the current width. Without the overflow
      check, slick's infinite clones duplicate short product lists. */
  _update(remeasure) {
    const needsCarousel = this._needsCarousel();
    this.querySelectorAll(".products-carousel__nav").forEach((button) => {
      const isPrev = button.classList.contains("products-carousel__nav--prev");
      button.toggleAttribute("hidden", !needsCarousel || (isPrev && !this._prevRevealed));
    });
    const initialized = this._$track.hasClass("slick-initialized");
    if (!needsCarousel) {
      if (initialized) {
        this._$track.slick("unslick");
      }
      return;
    }
    if (!initialized) {
      this._$track.slick(this._slickConfig());
    } else if (remeasure) {
      const currentSlide = this._$track.slick("slickCurrentSlide");
      this._$track.slick("unslick");
      this._$track.slick(this._slickConfig(currentSlide));
    }
    this._positionNav();
  }

  _slickConfig(initialSlide) {
    return {
      arrows: false,
      dots: false,
      infinite: true,
      speed: 300,
      initialSlide: initialSlide || 0,
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: false,
      variableWidth: true,
      touchThreshold: 10,
      responsive: [
        {
          breakpoint: 768,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            dots: false,
            centerMode: this.dataset.centerModeMobile === "true"
          }
        }
      ]
    };
  }

  /** Re-evaluate on width changes so the CSS-driven slide widths are
      re-measured. Height-only resizes (e.g. iOS URL bar) are ignored. */
  _bindResize() {
    this._lastWidth = window.innerWidth;
    let timer = null;
    this._onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (window.innerWidth === this._lastWidth) {
          return;
        }
        this._lastWidth = window.innerWidth;
        this._update(true);
      }, 150);
    };
    window.addEventListener("resize", this._onResize);
  }

  /** The prev arrow stays hidden until the user has moved the carousel
      (next click or swipe), then stays visible for the rest of the session. */
  _revealPrev() {
    if (this._prevRevealed) {
      return;
    }
    this._prevRevealed = true;
    const prevButton = this.querySelector(".products-carousel__nav--prev");
    if (prevButton && this._needsCarousel()) {
      prevButton.removeAttribute("hidden");
    }
  }

  _bindNavigation() {
    this.querySelectorAll(".products-carousel__nav").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        if (!this._$track.hasClass("slick-initialized")) {
          return;
        }
        const direction = button.classList.contains("products-carousel__nav--prev") ? "slickPrev" : "slickNext";
        this._$track.slick(direction);
      });
    });
  }

  _updateAriaHiddenFocusable() {
    const focusables = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
    this.querySelectorAll(".slick-slide").forEach((slide) => {
      const hidden = slide.getAttribute("aria-hidden") === "true";
      slide.querySelectorAll(focusables).forEach((element) => {
        if (hidden) {
          element.setAttribute("tabindex", "-1");
        } else {
          element.removeAttribute("tabindex");
        }
      });
    });
  }
};
if (!window.customElements.get("products-carousel")) {
  window.customElements.define("products-carousel", ProductsCarousel);
}
// This script loads without defer, one copy after each section's markup, so
// slick restructures the slides before their first paint. Components parsed
// after the first section upgrade on their START tag (children not parsed
// yet) and bail in connectedCallback - re-run it now their markup exists.
document.querySelectorAll("products-carousel").forEach((carousel) => {
  if (carousel.connectedCallback) {
    carousel.connectedCallback();
  }
});
